// The owner's stats page: headline numbers, both funnels with the time each step takes, activity
// over time and how splits are spread out. Guarded by ADMIN_TOKEN on the server; this phone
// remembers the key. Charts are plain HTML and SVG, so there's nothing extra to load.
import { fmt } from '../../money.ts';
import { brand, titled } from '../../brand.ts';
import type { Stats } from '../../stats.ts';
import { dur, type Bottleneck, type Health, type HealthMetric } from '../../health.ts';
import { adminStats } from '../api.ts';
import { app, logo, esc, inputValue, ls, target } from '../dom.ts';
import { onLeave } from '../router.ts';
import { handleShell, renderTab, tabsBar, type Tab } from './admin-tabs.ts';

type Bar = { label: string; count: number };
type Step = Stats['funnels']['payer'][number];

/** The host's share buttons, as the admin page names them. */
const SHARE_LABELS: Record<string, string> = { qr: 'QR code', group: 'Group text', text: 'Text it', copy: 'Copy link' };

const RANGES: Array<[days: number, label: string]> = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [0, 'All time']];

export const money = (c: number | null | undefined) => (c === null || c === undefined ? '–' : fmt(Math.round(c)));
export const num = (n: number | null | undefined, unit = '') => (n === null || n === undefined ? '–' : `${n}${unit}`);

export const panel = (title: string, body: string, note = '', cls = '') =>
  `<section class="card panel ${cls}"><div class="lbl">${esc(title)}</div>${body}${note ? `<p class="sub pnote">${note}</p>` : ''}</section>`;

/**
 * A headline number. `info` is a short explainer the owner opens with the ⓘ: what it means, how
 * it's counted, and what moves it. It's our own HTML, never anything a visitor typed.
 */
export function kpi(label: string, value: string, sub = '', trend?: number | null, info = ''): string {
  const arrow = trend === undefined || trend === null ? '' : trend > 0 ? `<span class="up">▲ ${trend}%</span>` : trend < 0 ? `<span class="down">▼ ${-trend}%</span>` : '<span class="sub">same</span>';
  const more = info ? `<details class="kpi-more"><summary aria-label="What ${esc(label)} means">i</summary></details>` : '';
  return `<div class="kpi">${more}<div class="sub">${esc(label)}</div><div class="kpi-v amt">${esc(value)}</div><div class="kpi-s">${arrow} ${esc(sub)}</div>${info ? `<div class="kpi-info">${info}</div>` : ''}</div>`;
}

/** Percent change from before to now, or null when there's nothing to compare. */
const change = (now: number | null, before: number | null | undefined) =>
  now === null || before === null || before === undefined || before === 0 ? null : Math.round(((now - before) / before) * 100);

const VERDICT: Record<Health['verdict'], [label: string, cls: string]> = {
  healthy: ['Healthy', 'good'], okay: ['Okay, with things to watch', 'watch'], 'needs work': ['Needs work', 'bad'],
  'at risk': ['At risk', 'bad'], 'too early': ['Too early to tell', 'unknown'],
};

function healthCard(h: Health): string {
  const [label, cls] = VERDICT[h.verdict];
  const tile = (m: HealthMetric) => `
    <div class="hm ${m.status}">
      <div class="hm-top"><span class="dot"></span><span class="sub">${esc(m.label)}</span></div>
      <div class="hm-v amt">${esc(m.value)}</div>
      <div class="hm-t">target ${esc(m.target)}${m.status === 'unknown' ? ' · not enough data' : ''}</div>
      <div class="hm-why">${esc(m.why)}</div>
    </div>`;
  return `<section class="card panel">
      <div class="verdict"><span class="pill ${cls}">${esc(label)}</span><span>${esc(h.headline)}</span></div>
      <div class="hms">${h.metrics.map(tile).join('')}</div>
    </section>`;
}

function bottleneckList(list: Bottleneck[], minSample: number): string {
  if (!list.length) return `<p class="sub empty">No bottlenecks in this range. Steps with fewer than ${minSample} people aren't judged yet.</p>`;
  return `<ol class="bns">${list.slice(0, 8).map((b, i) => `
    <li class="bn">
      <div class="bn-head"><b class="bn-n">${i + 1}</b><div><div class="bn-t">${esc(b.title)}</div><div class="sub">${esc(b.where)}</div></div><span class="pill ${b.severity === 'high' ? 'bad' : b.severity === 'medium' ? 'watch' : 'unknown'}">${esc(b.severity)}</span></div>
      <p class="bn-e">${esc(b.evidence)}</p>
      <div class="sub" style="font-weight:600">Try</div>
      <ul class="bn-f">${b.fixes.map((f) => `<li>${esc(f)}</li>`).join('')}</ul>
    </li>`).join('')}</ol>`;
}

/** Each step as a bar sized against the first, with what share of the step before made it and how long it took. */
function funnel(steps: Step[], flagged: Set<string> = new Set()): string {
  const top = Math.max(1, steps[0]?.count || 0, ...steps.map((s) => s.count));
  return `<ol class="funnel">${steps.map((s, i) => {
    const prev = i ? steps[i - 1].count : null;
    const conv = prev ? Math.round((s.count / prev) * 100) : null;
    const w = Math.max(2, Math.round((s.count / top) * 100));
    return `<li>
      <div class="f-head"><span class="f-step">${flagged.has(s.step) ? '<span class="flag" title="Bottleneck">⚑</span> ' : ''}${esc(s.step)}</span>
        <span class="f-nums"><b class="amt">${s.count}</b>${i ? `<span class="f-conv ${conv !== null && conv < 50 ? 'low' : ''}">${conv === null ? '–' : conv + '%'}</span>` : ''}</span></div>
      <div class="f-track"><i style="width:${w}%"></i></div>
      <div class="f-time sub">${[s.secs === null ? '' : `⏱ ${esc(dur(s.secs))} from the step before`, esc(s.note)].filter(Boolean).join(' · ')}</div>
    </li>`;
  }).join('')}</ol>`;
}

/** Horizontal bars, one per label. */
export function hbars(rows: Bar[], fmtCount: (n: number) => string = String): string {
  const top = Math.max(1, ...rows.map((r) => r.count));
  const total = rows.reduce((a, r) => a + r.count, 0);
  if (!total) return '<p class="sub empty">Nothing yet</p>';
  return `<div class="hbars">${rows.map((r) => `
    <div class="hb"><span class="hb-l">${esc(r.label)}</span>
      <span class="hb-t"><i style="width:${Math.round((r.count / top) * 100)}%"></i></span>
      <span class="hb-n amt">${esc(fmtCount(r.count))} <span class="sub">${Math.round((r.count / total) * 100)}%</span></span></div>`).join('')}</div>`;
}

/** Stacked daily columns: splits, friends joining, payments. */
function activity(daily: Stats['daily']): string {
  const W = 600, H = 150, pad = 20;
  const keys = [['splits', 'var(--ink)'], ['joins', 'var(--accent)'], ['paid', 'var(--owed)']] as const;
  const top = Math.max(1, ...daily.map((d) => d.splits + d.joins + d.paid));
  const bw = (W - pad) / daily.length;
  const cols = daily.map((d, i) => {
    let y = H;
    return keys.map(([k, color]) => {
      const h = (d[k] / top) * (H - 10);
      y -= h;
      return h ? `<rect x="${(pad + i * bw + bw * 0.12).toFixed(1)}" y="${y.toFixed(1)}" width="${(bw * 0.76).toFixed(1)}" height="${h.toFixed(1)}" fill="${color}" rx="1.5"><title>${esc(d.date)}: ${d.splits} splits, ${d.joins} joined, ${d.paid} paid, ${d.visits} visits</title></rect>` : '';
    }).join('');
  }).join('');
  const label = (i: number) => `<text x="${i === daily.length - 1 ? W : (pad + i * bw + bw / 2).toFixed(1)}" y="${H + 14}" text-anchor="${i === daily.length - 1 ? 'end' : i === 0 ? 'start' : 'middle'}">${esc(daily[i].date.slice(5))}</text>`;
  const ticks = daily.length > 1 ? [0, Math.floor((daily.length - 1) / 2), daily.length - 1].map(label).join('') : '';
  return `<svg class="chart" viewBox="0 0 ${W} ${H + 18}" role="img" aria-label="Activity per day">
      <line x1="${pad}" x2="${W}" y1="${H}" y2="${H}" stroke="var(--line)"/>
      <text x="0" y="12">${top}</text><text x="0" y="${H}">0</text>
      ${cols}${ticks}
    </svg>
    <div class="legend">${keys.map(([k, c]) => `<span><i style="background:${c}"></i>${k === 'splits' ? 'Splits' : k === 'joins' ? 'Friends joined' : 'Payments'}</span>`).join('')}</div>`;
}

/** One bar split into colored parts, for "how did scans go". */
function stacked(parts: Array<[label: string, n: number, color: string]>): string {
  const total = parts.reduce((a, p) => a + p[1], 0);
  if (!total) return '<p class="sub empty">No scans yet</p>';
  return `<div class="stack-bar">${parts.filter((p) => p[1]).map(([l, n, c]) => `<i style="flex:${n};background:${c}" title="${esc(l)}: ${n}"></i>`).join('')}</div>
    <div class="legend">${parts.map(([l, n, c]) => `<span><i style="background:${c}"></i>${esc(l)} <b class="amt">${n}</b></span>`).join('')}</div>`;
}

export const table = (head: string[], rows: Array<Array<string | number>>) => `
  <div class="tbl-wrap"><table class="tbl">
    <thead><tr>${head.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead>
    <tbody>${rows.length ? rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`).join('') : `<tr><td colspan="${head.length}" class="sub">Nothing yet</td></tr>`}</tbody>
  </table></div>`;

export function adminView(): void {
  document.title = titled('Stats');
  app.classList.add('wide');
  onLeave(() => app.classList.remove('wide'));
  const token = ls.get<string>('adminToken', '');
  if (!token) return askForKey('');
  const tab = ls.get<Tab>('adminTab', 'overview');
  if (tab !== 'overview') return renderTab(tab, token);
  const days = ls.get<number>('adminDays', 30);
  if (!app.querySelector('.dash')) app.innerHTML = `<div class="bar">${logo}<span>Stats</span></div><div class="body"><p class="loading">Loading…</p></div>`;
  adminStats(token, days)
    .then((s) => show(s))
    .catch((err: Error) => { if (/admin key|off/i.test(err.message)) ls.set('adminToken', ''); askForKey(err.message); });
}

function askForKey(error: string): void {
  app.innerHTML = `
    <div class="bar">${logo}<span>Stats</span></div>
    <form class="body stack narrow" id="keyForm" autocomplete="off">
      <h1 class="h">Owner stats</h1>
      <label class="lbl" for="adminKey">Admin key</label>
      <input id="adminKey" class="input" type="password" autocomplete="current-password">
      ${error ? `<div class="error">${esc(error)}</div>` : ''}
      <button class="btn">Open</button>
    </form>`;
  app.onsubmit = (e) => {
    e.preventDefault();
    const k = inputValue('adminKey');
    if (!k) return;
    ls.set('adminToken', k);
    adminView();
  };
}

/** The K-factor explainer, with this range's own numbers in it. */
function kInfo(s: Stats): string {
  const k = s.loop.k, before = s.kpis.prevKFactor;
  return `<b>How many new payers each payer brings in.</b> Every dinner shows ${brand.name} to the friends at the table. Some of them later pay a bill of their own after tapping "Start a split".
    <div class="kpi-calc">${k.newPayersBrought} new payer${k.newPayersBrought === 1 ? '' : 's'} ÷ ${k.payers} payer${k.payers === 1 ? '' : 's'} = <b>${num(k.value)}</b></div>
    It's also <b>friends per payer</b> (${num(k.friendsPerPayer)}) × <b>% of those friends who became payers</b> (${num(k.friendToPayerPct, '%')}). Raise either one and K goes up.
    <ul>
      <li><b>Above 1:</b> each payer brings more than one more, so it grows on its own.</li>
      <li><b>Below 1:</b> the loop adds free growth on top of what posts and creators bring in.</li>
    </ul>
    ${!s.days ? 'Pick 7 or 30 days to compare with the period before.' : before === null || before === undefined ? 'No dinners in the period before to compare with.' : `The ${s.days} days before: ${num(before)}.`} The newest dinners haven't had time to bring theirs in yet, so short ranges read low.`;
}

function show(s: Stats & { health: Health }): void {
  const k = s.kpis, sc = s.scans, lo = s.loyalty, h = s.health;
  const flagged = (who: string) => new Set(h.bottlenecks.filter((b) => b.where.startsWith(who + ':')).map((b) => b.where.split('→ ')[1]));
  const trend = k.prevSplits ? Math.round(((k.splits - k.prevSplits) / k.prevSplits) * 100) : null;
  const rangeName = s.days ? `last ${s.days} days` : 'all time';
  app.innerHTML = `
    <div class="bar">${logo}<span>Stats</span></div>
    <div class="body dash">
      ${tabsBar('overview')}
      <div class="dash-head">
        <div>
          <h1 class="h">How ${brand.name} is doing</h1>
          <p class="sub" style="margin:4px 0 0">${esc(rangeName)} · updated ${esc(new Date(s.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }))}</p>
        </div>
        <div class="chips range">${RANGES.map(([d, l]) => `<button class="chip" data-days="${d}" aria-pressed="${d === s.days}">${l}</button>`).join('')}</div>
      </div>

      ${healthCard(h)}

      ${panel('Bottlenecks to fix, biggest first', bottleneckList(h.bottlenecks, h.minSample), 'Ranked by how many people each one costs. Targets live in src/health.ts. Tune them as real numbers come in.')}

      <div class="kpis">
        ${kpi('Splits', String(k.splits), k.prevSplits === null ? '' : `vs ${k.prevSplits} before`, trend,
          'Bills a payer scanned or typed in and shared, in the range you picked. The arrow compares with the same number of days just before. Pick 7 or 30 days to see the arrow; all time has nothing to compare with.')}
        ${kpi('People at the table', String(k.people), '', undefined,
          `Everyone on those splits, payer included: friends who joined from the QR code or link, plus anyone the payer added by hand. Each one saw ${brand.name} at the table.`)}
        ${kpi('Bills split', money(k.billCents), '', undefined,
          'The total of every bill in the range: food, tax, fees and tip. It\'s money that moved between friends, not money we hold or earn.')}
        ${kpi('Thanks friends sent', money(k.thanksCents), 'where a fee could come from', undefined,
          'The optional $1 thanks friends add for the payer when it\'s turned on, counted for friends who paid. It shows how much people will pay on top of their share, which is where a per-split fee could come from later.')}
        ${kpi('Said yes to restaurant texts', String(k.smsOptIns), k.smsOptInConversionPct === null ? 'conversion shows once the card is seen' : `${k.smsOptInConversionPct}% of ${k.smsOptInOffered} who saw the offer`, undefined,
          'Guests who ticked "Want deals from this restaurant?" and gave a number. The % is yeses out of phones that were shown the card. This is the list restaurants would pay for.')}
        ${kpi('Survey answers', String(k.surveyResponses), 'for restaurants, after the bill', undefined,
          'Guests who answered the short survey after paying (rating, occasion, what would bring them back). Restaurants see these on their own dashboard in the Restaurants tab.')}
        ${kpi('K-factor', num(k.kFactor), s.loop.k.value === null ? 'no payers yet' : `${num(s.loop.k.friendsPerPayer)} friends per payer × ${num(s.loop.k.friendToPayerPct, '%')} became payers`, change(k.kFactor, k.prevKFactor), kInfo(s))}
        ${kpi('New payers from the table loop', String(k.loopNewPayers), k.loopSharePct === null ? 'no new payers yet' : `${k.loopSharePct}% of ${k.newPayers} new payers`, undefined,
          'People whose very first split came from tapping "Start a split" after paying a friend back. The % is their share of all first-time payers in the range. The higher it is, the less growth depends on posts and ads.')}
        ${kpi('Payers with a card saved', String(k.payersWithCard), k.cardPct === null ? 'turns on with Stripe' : `${k.cardPct}% of payers · turns on with Stripe`, undefined,
          'Payers who saved a card for the future per-split fee. Stays at 0 until Stripe card saving is switched on.')}
      </div>

      ${panel('Landing pages', table(['Page', 'Visitors', 'Tapped start', 'Started a split', 'Shared a split', 'Conversion'], s.landing.map((l) => [
        `${l.name} (${l.path})`, l.visitors, l.tapped, l.started, l.created, num(l.conversionPct, '%'),
      ])) + hbars(s.landing.map((l) => ({ label: l.name, count: l.created }))),
        'By the page a phone first landed on. Conversion is splits shared ÷ visitors. Give each video or post its own code too, in Links &amp; codes.')}

      ${panel('$1 test (points and both pages)', table(['Side', 'Visitors', 'Tapped start', 'Shared a split', 'Conversion', 'Kept the $1 on', 'Friends paid', 'Earned'], s.feeTest.map((f) => [
        f.name, f.visitors, f.tapped, f.created, num(f.conversionPct, '%'), f.withFee, f.friendPaidPct === null ? '–' : `${f.friendsPaid} of ${f.friendsJoined} (${f.friendPaidPct}%)`, money(f.feeCents),
      ])), 'Phones that first landed on /l/points or /l/both get one side at random and keep it. "No $1" phones never see it and the switch starts off. With small numbers, a gap of a few points is noise.')}

      <div class="grid2">
        ${panel('Payer funnel', funnel(s.funnels.payer, flagged('Payer')), `Each % is how many made it from the step before. ${s.funnelSince ? `Counted since ${esc(new Date(s.funnelSince).toLocaleDateString())}.` : ''}`)}
        ${panel('Friend funnel', funnel(s.funnels.friend, flagged('Friend')), 'From scanning the QR code to paying the payer back.')}
      </div>

      <div class="grid2">
        ${panel('Group text button', funnel(s.groupShare.funnel), 'Taps on "Send to the group text" and "Save this for next time", by test arm. Half of tables get the group text as the main button, half keep the QR code first.')}
        ${panel('Table loop', funnel(s.loop.funnel), `Friends who paid tap "Start a split" and run their own dinner. ${s.loop.friendsReached} friends joined those dinners, the next turn of the loop. K-factor ${num(s.loop.k.value)}: the ${s.loop.k.payers} payers in this range brought ${s.loop.k.newPayersBrought} new payers. Above 1, every payer brings more than one more and it grows on its own. Recent dinners haven't had time to bring theirs yet, so this week's number reads low.`)}
        ${panel('Dinners that started the most new splits', table(['Payer\'s code', 'Splits started', 'New payers', 'Friends reached'], s.loop.starters.map((r) => [r.who, r.splits, r.newPayers, r.friendsReached])),
          'Each payer gets a referral code on their first split. Find them by code in People or Links &amp; codes, and thank them first.')}
      </div>

      ${panel('Activity', activity(s.daily))}

      <div class="grid3">
        ${panel('People per split', hbars(s.charts.peoplePerSplit))}
        ${panel('Time until everyone paid', hbars(s.charts.timeToAllPaid))}
        ${panel('Tip, as % of the food', hbars(s.charts.tipPct))}
        ${panel('Bill size', hbars(s.charts.billSize))}
        ${panel('Splits per payer', hbars(lo.splitsPerPayer), `${lo.friendsWhoLaterPaidABill} friends later paid a bill themselves. ${lo.phonesBackAnotherDay} of ${lo.phones} phones came back another day.`)}
        ${panel('Receipt reading', stacked([
          ['Read first time', sc.ok - sc.rechecked, 'var(--accent)'],
          ['Fixed on a second look', sc.rechecked - sc.stillOff, 'var(--accent-deep)'],
          ["Didn't add up", sc.stillOff, 'var(--owed)'],
          ['Failed', sc.failed, 'var(--ink-3)'],
        ]), `Median ${num(sc.medianSecs, 's')}, slowest 10% ${num(sc.p90Secs, 's')}.${Object.keys(sc.errors).length ? ' Errors: ' + esc(Object.entries(sc.errors).map(([e, n]) => `${e} (${n})`).join(', ')) : ''}`)}
      </div>

      <div class="grid2">
        ${panel('Where people came from', hbars(s.sources.map((r) => ({ label: r.source, count: r.splits + r.joins }))) + table(['Source', 'Visits', 'Splits', 'Joins'], s.sources.map((r) => [r.source, r.visits, r.splits, r.joins])),
          `Splits plus joins per source.${s.shareChannels ? ' Joins by share button: ' + s.shareChannels.map((c) => `${SHARE_LABELS[c.channel]} ${c.joins} (${c.taps} ${c.taps === 1 ? 'tap' : 'taps'})`).join(' · ') + '.' : ''}${Object.keys(s.campaigns).length ? ' Campaigns: ' + esc(Object.entries(s.campaigns).map(([c, n]) => `${c} (${n})`).join(', ')) + '.' : ''} Tag links with ?utm_source=instagram&amp;utm_campaign=launch.`)}
        ${panel('Phones', hbars(Object.entries(s.charts.phones).map(([label, count]) => ({ label: label === 'ios' ? 'iPhone' : label === 'android' ? 'Android' : 'Computer', count }))))}
      </div>

      ${panel('Restaurants', table(['Restaurant', 'Splits', 'Avg party', 'Avg bill', 'Tip %', 'Groups back'], s.venues.map((v) => [
        v.venue, v.splits, num(v.avgPartySize), money(v.avgBillCents), num(v.medianTipPct, '%'), v.groupsBack,
      ])), 'Open the Restaurants tab for each place\'s own dashboard and text list.')}

      ${panel('Recent splits', table(['Code', 'Where', 'When', 'People', 'Bill', 'Paid', 'Closed in', 'Status'], s.recent.map((r) => [
        r.code + (r.repeatPayer ? ' ↻' : ''), r.venue, new Date(r.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' }),
        r.people, money(r.billCents), r.paid, r.minsToClose === null ? '–' : dur(r.minsToClose * 60), r.status,
      ])), '↻ marks a payer with more than one split.')}

      <div class="row2 narrow"><button class="btn2" data-act="refresh">Refresh</button><button class="btn2" data-act="forget">Sign out</button></div>
    </div>`;
  app.onclick = (e) => {
    if (handleShell(e)) return;
    const r = target<HTMLButtonElement>(e, '[data-days]');
    if (r) { ls.set('adminDays', Number(r.dataset.days)); return adminView(); }
    if (target(e, '[data-act=refresh]')) adminView();
    if (target(e, '[data-act=forget]')) { ls.set('adminToken', ''); adminView(); }
  };
}
